import type { OrbEvent } from "@eldrun/protocol";
import { Mesh, type Object3D, type ShaderMaterial } from "three";
import { SKILL_SHAPES } from "../render/geometry/skill-shapes.ts";
import { createSkillMeshMaterial } from "../render/shaders/skill-mesh.ts";

interface Flying {
  mesh: Mesh;
  material: ShaderMaterial;
  e: OrbEvent;
  /** Instante (relógio local) em que o orbe saiu no servidor. */
  born: number;
  /** Segundos até o alcance. */
  life: number;
}

/** Posição do orbe `t` segundos depois de sair: reto, sem gravidade (o mesmo voo do servidor). */
export function orbAt(e: Pick<OrbEvent, "x" | "y" | "z" | "vx" | "vy" | "vz">, t: number): { x: number; y: number; z: number } {
  return { x: e.x + e.vx * t, y: e.y + e.vy * t, z: e.z + e.vz * t };
}

/**
 * Orbes do cajado em voo (F98): um nó da rede girando, na cor de quem atirou, no programa das peças das
 * habilidades (nenhum programa novo). O servidor resolve o acerto; aqui só o desenho, que sai no fim
 * (`end`) ou no alcance.
 */
export class OrbLayer {
  private readonly root: Object3D;
  private readonly flying = new Map<string, Flying>();

  constructor(root: Object3D) {
    this.root = root;
  }

  /** Orbe anunciado: nasce meio RTT atrás, para chegar ao ponto junto com o do servidor. */
  spawn(e: OrbEvent, color: string, now: number, rtt: number, alpha = 1): void {
    const speed = Math.hypot(e.vx, e.vy, e.vz);
    if (alpha <= 0 || speed <= 0) return;
    const material = createSkillMeshMaterial(color, 3, 0.6);
    material.uniforms["uAlpha"]!.value = alpha;
    const mesh = new Mesh(SKILL_SHAPES.no, material);
    mesh.renderOrder = 6;
    mesh.scale.setScalar(0.9);
    this.root.add(mesh);
    this.flying.set(e.id, { mesh, material, e, born: now - rtt / 2, life: e.range / speed });
    this.place(this.flying.get(e.id)!, now);
  }

  /** O servidor disse onde o orbe parou: some. */
  end(id: string): void {
    const f = this.flying.get(id);
    if (f) this.remove(id, f);
  }

  update(now: number): void {
    for (const [id, f] of this.flying) {
      if ((now - f.born) / 1000 >= f.life) this.remove(id, f);
      else this.place(f, now);
    }
  }

  get count(): number {
    return this.flying.size;
  }

  dispose(): void {
    for (const [id, f] of this.flying) this.remove(id, f);
  }

  private place(f: Flying, now: number): void {
    const t = Math.max(0, (now - f.born) / 1000);
    const p = orbAt(f.e, t);
    f.mesh.position.set(p.x, p.y, p.z);
    f.mesh.rotation.set(t * 7, t * 11, 0);
  }

  private remove(id: string, f: Flying): void {
    this.root.remove(f.mesh);
    f.material.dispose();
    this.flying.delete(id);
  }
}
